import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // The design system's buttons: square, 11-12 px, a dark primary with a
  // little letter-spacing, outlines on the card surface with a hairline,
  // ghosts for icons; danger is an outline with the destructive colour.
  // Sizes are written as text-[12px], not the text-nav scale: these classes
  // go through cn() (tailwind-merge), which does not know the custom scale
  // and would drop a text colour next to it as a conflicting colour.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[2px] text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground tracking-[0.04em] hover:bg-primary/90",
        destructive: "border border-destructive/40 bg-card text-destructive hover:bg-danger-surface",
        outline:
          "border border-border bg-card text-foreground hover:bg-secondary",
        secondary: "bg-secondary text-secondary-foreground hover:bg-accent",
        ghost: "text-muted-foreground hover:bg-secondary hover:text-foreground",
        link: "text-signal underline-offset-4 hover:underline",
        /** A module's action: mono 10 px, outline. */
        action: "border border-border bg-card font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:bg-secondary hover:text-foreground",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-7 px-2.5 text-[11px]",
        lg: "h-11 px-6 text-[12px]",
        icon: "h-7 w-7",
        xs: "h-6 px-2 text-[9px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
